import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';

export default function WishlistPage() {
  const { wishlist, removeFromWishlist, addToCart } = useApp();
  const navigate = useNavigate();
  usePageTitle('Wishlist');

  const handleAddToCart = async (product) => {
    const hasOptions = (product.option_types || []).length > 0;
    if (hasOptions) {
      navigate(`/product/${product.id}`);
      toast.info('Please choose options for this item');
      return;
    }
    try {
      await addToCart(product.id, 1, product.variants?.[0]?.id || null);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const handleRemove = (productId) => {
    removeFromWishlist(productId);
    toast.success('Removed from wishlist');
  };

  if (wishlist.items.length === 0) {
    return (
      <div className="page-transition min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <Heart className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
          <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your wishlist is empty
          </h1>
          <p className="text-muted-foreground mb-8">
            Save your favorite items to your wishlist for later.
          </p>
          <Link to="/shop" data-testid="empty-wishlist-shop-link">
            <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full px-8">
              Start Shopping
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="bg-[#0B1120] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="breadcrumb mb-4">
            <Link to="/" className="text-white/60 hover:text-white">Home</Link>
            <span className="text-white/40">/</span>
            <span className="text-white">Wishlist</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="wishlist-title">
            My Wishlist
          </h1>
          <p className="text-white/70 mt-2">{wishlist.items.length} items</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="wishlist-items">
          {wishlist.items.map((product) => {
            const variants = product.variants || [];
            const isOnSale = variants.some((v) => v.sale_price && v.sale_price < v.price);
            const hasRange = product.price_to > product.price_from;
            const outOfStock = (product.total_stock ?? 0) === 0;

            return (
              <div 
                key={product.id} 
                className="bg-white rounded-xl border border-border overflow-hidden group"
                data-testid={`wishlist-item-${product.id}`}
              >
                {/* Image */}
                <Link to={`/product/${product.id}`} className="block relative aspect-[3/4] overflow-hidden">
                  {isOnSale && (
                    <span className="sale-badge">Sale</span>
                  )}
                  <img
                    src={product.images?.[0] || 'https://images.unsplash.com/photo-1559709451-1b5dc3c0038c?w=400'}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </Link>

                {/* Content */}
                <div className="p-4">
                  <Link to={`/product/${product.id}`}>
                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">
                      {product.category}
                    </p>
                    <h3 className="font-medium text-foreground mb-2 hover:text-[#EA580C] transition-colors line-clamp-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {product.name}
                    </h3>
                  </Link>

                  {/* Price */}
                  <div className="flex items-center gap-2 mb-4">
                    {hasRange && <span className="text-xs text-muted-foreground">from</span>}
                    <span className="font-semibold text-[#EA580C]">
                      {formatPrice(product.price_from)}
                    </span>
                    {isOnSale && !hasRange && (
                      <span className="text-xs text-green-600">On sale</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAddToCart(product)}
                      disabled={outOfStock}
                      className="flex-1 bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full text-sm"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <ShoppingCart className="h-4 w-4 mr-1" />
                      {outOfStock ? 'Out of Stock' : 'Add to Cart'}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleRemove(product.id)}
                      className="rounded-full"
                      data-testid={`remove-wishlist-${product.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
